import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth }           from '@/contexts/AuthContext';
import FilterBar             from '@/components/FilterBar';
import type { FilterField }  from '@/components/FilterBar';
import PaginationBar         from '@/components/PaginationBar';
import SortableTh            from '@/components/SortableTh';
import ExportButton          from '@/components/ExportButton';
import type { ExportScope }  from '@/components/ExportButton';
import { useSort }           from '@/hooks/useSort';
import { exportData, exportFmtDate } from '@/utils/export';
import type { ExportFormat } from '@/utils/export';
import { formatDateDMY } from '@/utils/date';
import {
  listNonParishCemeteries, createNonParishCemetery,
  getNonParishCemetery, setNonParishSubscription, deleteNonParishCemetery,
  listNonParishCemeteryMembers, saveNonParishCemeteryMember, deleteNonParishCemeteryMember,
  findNonParishCemeteryByCode,
} from '@/api/nonParishCemetery';
import { validatePhone } from '@/utils/validation';
import type { NonParishCemeteryRecord, NonParishCemeteryMember } from '@/api/nonParishCemetery';

const FILTER_FIELDS: FilterField[] = [
  { key: 'search',     label: 'Search', type: 'text', placeholder: 'Name, grave number, or contact…' },
  { key: 'burialFrom', label: 'Burial from', type: 'date' },
  { key: 'burialTo',   label: 'Burial to',   type: 'date' },
];
const DEFAULT_FILTERS = {
  search:     null as null | string,
  burialFrom: null as null | string,
  burialTo:   null as null | string,
};
const filterParams = (f: typeof DEFAULT_FILTERS) => ({
  search:     f.search ?? undefined,
  burialFrom: f.burialFrom ?? undefined,
  burialTo:   f.burialTo ?? undefined,
});
const currentYear = new Date().getFullYear();
// Same range as the main Subscriptions page (2023 onward, plus one year ahead)
// so admins can record/back-fill non-parish subscriptions for any year the
// parish has been tracking them, not just a rolling 3-year window.
const SUB_START_YEAR = 2023;
const YEAR_OPTIONS = Array.from(
  { length: currentYear - SUB_START_YEAR + 2 },
  (_, i) => SUB_START_YEAR + i,
);

const fmtDate = formatDateDMY;

export default function NonParishCemeteryPage() {
  const { isAdmin } = useAuth();

  const [rows,       setRows]       = useState<NonParishCemeteryRecord[]>([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [fetchErr,   setFetchErr]   = useState('');

  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters,  setFilters]  = useState(DEFAULT_FILTERS);

  // Add/edit-record form
  const [showAdd, setShowAdd] = useState(false);
  const [editId,  setEditId]  = useState<number | null>(null);
  const [form, setForm] = useState({
    name: '', gender: '', dob: '', graveNumber: '',
    dateOfDeath: '', dateOfBurial: '', contactPerson: '', contactPhone: '', notes: '',
  });
  const [saving,    setSaving]    = useState(false);
  const [saveErr,   setSaveErr]   = useState('');
  const [phoneErr,  setPhoneErr]  = useState('');

  // Duplicate grave-code detection on the Add form — lets staff attach a
  // second person to an existing grave (e.g. code "K7") instead of creating
  // a second, separately-billed grave with the same code.
  const [dupMatch,    setDupMatch]    = useState<NonParishCemeteryRecord | null>(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [dupAttaching, setDupAttaching] = useState(false);

  // Subscription expand
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [subYear,    setSubYear]    = useState(currentYear);
  const [subAmount,  setSubAmount]  = useState('');
  const [subDate,    setSubDate]    = useState('');
  const [subStatus,  setSubStatus]  = useState<'Paid' | 'Unpaid' | 'Pending'>('Pending');
  const [subLoading, setSubLoading] = useState(false);
  const [subSaving,  setSubSaving]  = useState(false);
  const [subErr,     setSubErr]     = useState('');

  // Members on the expanded grave (a grave can hold more than one person)
  const [members,        setMembers]        = useState<NonParishCemeteryMember[]>([]);
  const [showAddMember,  setShowAddMember]  = useState(false);
  const [editMemberId,   setEditMemberId]   = useState<number | null>(null);
  const [memberForm, setMemberForm] = useState({
    name: '', gender: '', dob: '', dateOfDeath: '', dateOfBurial: '', notes: '',
  });
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberErr,    setMemberErr]    = useState('');
  const [memberDeletingId, setMemberDeletingId] = useState<number | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<NonParishCemeteryRecord | null>(null);
  const [deleteErr,    setDeleteErr]    = useState('');
  const [deleting,     setDeleting]     = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchErr('');
    try {
      const result = await listNonParishCemeteries({ page, pageSize, ...filterParams(filters) });
      setRows(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      setFetchErr('Failed to load non-parish cemetery records. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const confirmDelete = (row: NonParishCemeteryRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(row);
    setDeleteErr('');
    if (expandedId === row.nonparish_cemetery_id) setExpandedId(null);
  };
  const cancelDelete = () => { setDeleteTarget(null); setDeleteErr(''); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteNonParishCemetery(deleteTarget.nonparish_cemetery_id);
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setDeleteErr(err?.response?.data?.error ?? 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async (format: ExportFormat, scope: ExportScope) => {
    const result = await listNonParishCemeteries({ page: 1, pageSize: 9999, ...(scope === 'filtered' ? filterParams(filters) : {}) });
    exportData({
      title:    'Non-Parish Cemetery Records',
      filename: 'non-parish-cemetery-export',
      columns: [
        { label: 'Name',           key: 'name'           },
        { label: 'Gender',         key: 'gender'         },
        { label: 'Date of Birth',  key: 'dob',            format: exportFmtDate },
        { label: 'Grave Code',     key: 'grave_number'   },
        { label: 'Date of Death',  key: 'date_of_death',  format: exportFmtDate },
        { label: 'Date of Burial', key: 'date_of_burial', format: exportFmtDate },
        { label: 'Contact Person', key: 'contact_person' },
        { label: 'Contact Phone',  key: 'contact_phone'  },
        { label: 'Notes',          key: 'notes'          },
      ],
      rows: result.items as unknown as Record<string, unknown>[],
      filters: {
        Search: filters.search ?? '',
        'Burial from': filters.burialFrom ?? '',
        'Burial to': filters.burialTo ?? '',
      },
    }, format);
  };

  const { sorted: sortedRows, sort, toggleSort } = useSort(rows as unknown as Record<string, unknown>[]);
  const displayRows = sortedRows as unknown as NonParishCemeteryRecord[];

  const handleFilterChange = (key: string, value: string | number | null) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
    setPage(1);
  };
  const handleReset    = () => { setFilters(DEFAULT_FILTERS); setPage(1); };
  const handlePageSize = (s: number) => { setPageSize(s); setPage(1); };

  const updateField = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ── Duplicate grave-code check — runs on Add AND Edit. Without this, an
  // admin editing a record's grave code to match another one gets no
  // warning at all, silently creating two separately-billed records for
  // the same physical grave (exactly the "same grave, two subscriptions"
  // bug this was added to catch).
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout>>();
  const checkGraveCode = (code: string) => {
    updateField('graveNumber', code);
    setDupMatch(null);
    clearTimeout(dupCheckTimer.current);
    if (!code.trim()) return;
    dupCheckTimer.current = setTimeout(async () => {
      setDupChecking(true);
      try {
        const match = await findNonParishCemeteryByCode(code.trim());
        // A match on the record's own (unchanged) code isn't a duplicate.
        setDupMatch(match && match.nonparish_cemetery_id !== editId ? match : null);
      } catch {
        // non-critical — silently skip the dup check on failure
      } finally {
        setDupChecking(false);
      }
    }, 400);
  };

  // Attach the person currently in the Add form as a member of the existing
  // grave found by checkGraveCode, instead of creating a second grave with
  // the same code.
  const attachAsMemberOfExisting = async () => {
    if (!dupMatch) return;
    if (!form.name.trim()) { setSaveErr('Name is required.'); return; }
    setDupAttaching(true);
    setSaveErr('');
    try {
      await saveNonParishCemeteryMember(dupMatch.nonparish_cemetery_id, {
        name: form.name.trim(),
        gender: form.gender || undefined,
        dob: form.dob || null,
        dateOfDeath: form.dateOfDeath || null,
        dateOfBurial: form.dateOfBurial || null,
        notes: form.notes || undefined,
      });
      const attachedId = dupMatch.nonparish_cemetery_id;
      resetForm();
      await fetchData();
      setExpandedId(attachedId);
      await loadSub(attachedId, subYear);
    } catch (err: any) {
      setSaveErr(err?.response?.data?.error ?? 'Failed to attach as a member of the existing grave.');
    } finally {
      setDupAttaching(false);
    }
  };

  const resetForm = () => {
    setShowAdd(false);
    setEditId(null);
    setForm({ name: '', gender: '', dob: '', graveNumber: '', dateOfDeath: '', dateOfBurial: '',
              contactPerson: '', contactPhone: '', notes: '' });
    setSaveErr('');
    setPhoneErr('');
    setDupMatch(null);
    setDupChecking(false);
  };

  const openEdit = (row: NonParishCemeteryRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAdd(true);
    setEditId(row.nonparish_cemetery_id);
    setForm({
      name:          row.name ?? '',
      gender:        row.gender ?? '',
      dob:           row.dob ? String(row.dob).slice(0, 10) : '',
      graveNumber:   row.grave_number ?? '',
      dateOfDeath:   row.date_of_death ? String(row.date_of_death).slice(0, 10) : '',
      dateOfBurial:  row.date_of_burial ? String(row.date_of_burial).slice(0, 10) : '',
      contactPerson: row.contact_person ?? '',
      contactPhone:  row.contact_phone ?? '',
      notes:         row.notes ?? '',
    });
    setSaveErr('');
    setPhoneErr('');
    setDupMatch(null);
    if (expandedId === row.nonparish_cemetery_id) setExpandedId(null);
  };

  const submitForm = async () => {
    if (!form.name.trim()) { setSaveErr('Name is required.'); return; }
    const pErr = validatePhone(form.contactPhone);
    setPhoneErr(pErr);
    if (pErr) return;
    setSaving(true);
    setSaveErr('');
    try {
      await createNonParishCemetery({
        nonParishCemeteryId: editId ?? undefined,
        name: form.name.trim(),
        gender: form.gender || undefined,
        dob: form.dob || null,
        graveNumber: form.graveNumber || undefined,
        dateOfDeath: form.dateOfDeath || null,
        dateOfBurial: form.dateOfBurial || null,
        contactPerson: form.contactPerson || undefined,
        contactPhone: form.contactPhone || undefined,
        notes: form.notes || undefined,
      });
      resetForm();
      fetchData();
    } catch (err: any) {
      setSaveErr(err?.response?.data?.error ?? 'Failed to save record.');
    } finally {
      setSaving(false);
    }
  };

  const loadSub = async (id: number, year: number) => {
    setSubLoading(true);
    setSubErr('');
    try {
      // One call gets both the subscription history and every member on
      // this grave — a grave can hold more than one person while still
      // having exactly one subscription per year.
      const { subscriptions, members: memberRows } = await getNonParishCemetery(id);
      const row = subscriptions.find((s) => s.subscription_year === year);
      setSubAmount(String(row?.amount ?? ''));
      setSubDate(row?.payment_date ? String(row.payment_date).slice(0, 10) : '');
      setSubStatus((row?.payment_status as 'Paid' | 'Unpaid' | 'Pending') || 'Pending');
      setMembers(memberRows);
    } catch {
      setSubErr('Failed to load subscription.');
    } finally {
      setSubLoading(false);
    }
  };

  const toggleExpand = async (row: NonParishCemeteryRecord) => {
    if (expandedId === row.nonparish_cemetery_id) {
      setExpandedId(null);
      setShowAddMember(false);
      setEditMemberId(null);
      return;
    }
    setExpandedId(row.nonparish_cemetery_id);
    setShowAddMember(false);
    setEditMemberId(null);
    await loadSub(row.nonparish_cemetery_id, subYear);
  };

  const changeSubYear = async (id: number, year: number) => {
    setSubYear(year);
    await loadSub(id, year);
  };

  const saveSub = async (id: number) => {
    setSubSaving(true);
    setSubErr('');
    try {
      await setNonParishSubscription(id, {
        year: subYear,
        amount: parseFloat(subAmount) || 0,
        paymentDate: subDate || null,
        paymentStatus: subStatus,
      });
    } catch (err: any) {
      setSubErr(err?.response?.data?.error ?? 'Failed to save subscription.');
    } finally {
      setSubSaving(false);
    }
  };

  // ── Members on the expanded grave ───────────────────────────────────────
  const updateMemberField = (key: keyof typeof memberForm, value: string) =>
    setMemberForm((prev) => ({ ...prev, [key]: value }));

  const startAddMember = () => {
    setShowAddMember(true);
    setEditMemberId(null);
    setMemberForm({ name: '', gender: '', dob: '', dateOfDeath: '', dateOfBurial: '', notes: '' });
    setMemberErr('');
  };

  const startEditMember = (m: NonParishCemeteryMember) => {
    setShowAddMember(true);
    setEditMemberId(m.member_id);
    setMemberForm({
      name:         m.name ?? '',
      gender:       m.gender ?? '',
      dob:          m.dob ? String(m.dob).slice(0, 10) : '',
      dateOfDeath:  m.date_of_death ? String(m.date_of_death).slice(0, 10) : '',
      dateOfBurial: m.date_of_burial ? String(m.date_of_burial).slice(0, 10) : '',
      notes:        m.notes ?? '',
    });
    setMemberErr('');
  };

  const cancelMemberForm = () => {
    setShowAddMember(false);
    setEditMemberId(null);
    setMemberErr('');
  };

  const saveMember = async (cemeteryId: number) => {
    if (!memberForm.name.trim()) { setMemberErr('Name is required.'); return; }
    setMemberSaving(true);
    setMemberErr('');
    try {
      await saveNonParishCemeteryMember(cemeteryId, {
        memberId: editMemberId ?? undefined,
        name: memberForm.name.trim(),
        gender: memberForm.gender || undefined,
        dob: memberForm.dob || null,
        dateOfDeath: memberForm.dateOfDeath || null,
        dateOfBurial: memberForm.dateOfBurial || null,
        notes: memberForm.notes || undefined,
      });
      const updated = await listNonParishCemeteryMembers(cemeteryId);
      setMembers(updated);
      setShowAddMember(false);
      setEditMemberId(null);
      fetchData(); // refresh member_count/member_names on the main grid row
    } catch (err: any) {
      setMemberErr(err?.response?.data?.error ?? 'Failed to save member.');
    } finally {
      setMemberSaving(false);
    }
  };

  const removeMember = async (cemeteryId: number, memberId: number) => {
    if (!window.confirm('Remove this member from the grave record?')) return;
    setMemberDeletingId(memberId);
    setMemberErr('');
    try {
      await deleteNonParishCemeteryMember(memberId);
      const updated = await listNonParishCemeteryMembers(cemeteryId);
      setMembers(updated);
      fetchData();
    } catch (err: any) {
      setMemberErr(err?.response?.data?.error ?? 'Failed to remove member.');
    } finally {
      setMemberDeletingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy font-georgia">Non-Parish Cemetery</h1>
          <p className="text-xs text-teal mt-0.5">
            Records for individuals buried outside the parish — click a row to manage subscriptions
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButton onExport={handleExport} hasFilters={!!(filters.search || filters.burialFrom || filters.burialTo)} />
          {isAdmin && !showAdd && (
            <button onClick={() => setShowAdd(true)} className="btn-primary">+ Add Record</button>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="bg-white border border-grid-border p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="font-georgia text-sm text-navy font-bold">{editId ? 'Edit Non-Parish Cemetery Record' : 'New Non-Parish Cemetery Record'}</p>
            <button onClick={resetForm} className="text-xs text-teal hover:text-navy underline">Cancel</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => updateField('name', e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Gender</label>
              <select value={form.gender} onChange={(e) => updateField('gender', e.target.value)}
                      className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal">
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Birth</label>
              <input type="date" value={form.dob} onChange={(e) => updateField('dob', e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Grave Code (max 6 chars)</label>
              <input type="text" maxLength={6} value={form.graveNumber} onChange={(e) => checkGraveCode(e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
              {dupChecking && <p className="text-xs text-teal mt-1">Checking for an existing grave with this code…</p>}
              {!dupChecking && dupMatch && (
                <div className="mt-2 bg-amber-50 border border-amber-300 rounded p-3 text-sm font-georgia">
                  <p className="text-navy">
                    Grave code <span className="font-bold">{form.graveNumber}</span> already exists —{' '}
                    <span className="font-bold">{dupMatch.member_names || dupMatch.name}</span> {(dupMatch.member_count ?? 1) > 1 ? 'are' : 'is'} already recorded there.
                  </p>
                  {editId ? (
                    <p className="text-teal text-xs mt-1">
                      That's a separate record with its own subscription. Two records sharing a grave code
                      means two separate bills for the same grave — instead, close this record and use
                      "+ Add member" on {dupMatch.member_names || dupMatch.name}'s record to add this person there.
                    </p>
                  ) : (
                    <>
                      <p className="text-teal text-xs mt-1">
                        Add {form.name.trim() || 'this person'} as another member of that same grave instead of creating a separate, separately-billed grave?
                      </p>
                      <button
                        onClick={attachAsMemberOfExisting}
                        disabled={dupAttaching || !form.name.trim()}
                        className="btn-primary text-xs px-3 py-1.5 mt-2 disabled:opacity-50"
                      >
                        {dupAttaching ? 'Adding…' : `Add as member of grave ${form.graveNumber}`}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Death</label>
              <input type="date" value={form.dateOfDeath} onChange={(e) => updateField('dateOfDeath', e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Burial</label>
              <input type="date" value={form.dateOfBurial} onChange={(e) => updateField('dateOfBurial', e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Contact Person</label>
              <input type="text" value={form.contactPerson} onChange={(e) => updateField('contactPerson', e.target.value)}
                     className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
            <div>
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Contact Phone</label>
              <input type="text" value={form.contactPhone}
                     maxLength={10}
                     onChange={(e) => { updateField('contactPhone', e.target.value); setPhoneErr(validatePhone(e.target.value)); }}
                     onBlur={(e) => setPhoneErr(validatePhone(e.target.value))}
                     placeholder="10-digit mobile"
                     className={`w-full border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal ${phoneErr ? 'border-red-400' : 'border-grid-border'}`} />
              {phoneErr && <p className="text-xs text-red-500 mt-1">{phoneErr}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Notes</label>
              <textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)} rows={2}
                        className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
            </div>
          </div>
          {saveErr && <p className="text-sm text-red-600 font-georgia mt-3">{saveErr}</p>}
          <button onClick={submitForm} disabled={saving} className="btn-primary mt-3">
            {saving ? 'Saving…' : editId ? 'Update Record' : 'Save Record'}
          </button>
        </div>
      )}

      <FilterBar fields={FILTER_FIELDS} values={filters} onChange={handleFilterChange} onReset={handleReset} />

      {fetchErr && <div className="text-sm text-red-600 font-georgia mb-4">{fetchErr}</div>}

      <div className="bg-white border border-grid-border overflow-x-auto">
        <div className="hidden sm:block">
        <table className="min-w-full text-sm font-georgia">
          <thead>
            <tr className="border-b border-grid-border bg-cream-light text-left text-xs text-teal uppercase tracking-wide">
              <SortableTh label="Name"           sortKey="name"           sort={sort} onSort={toggleSort} />
              <SortableTh label="Gender"         sortKey="gender"         sort={sort} onSort={toggleSort} />
              <SortableTh label="Grave No."      sortKey="grave_number"   sort={sort} onSort={toggleSort} />
              <SortableTh label="Date of Death"  sortKey="date_of_death"  sort={sort} onSort={toggleSort} />
              <SortableTh label="Date of Burial" sortKey="date_of_burial" sort={sort} onSort={toggleSort} />
              <SortableTh label="Contact"        sortKey="contact_person" sort={sort} onSort={toggleSort} />
              {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-6 text-center text-teal">Loading…</td></tr>
            )}
            {!loading && displayRows.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-6 text-center text-teal">No records found.</td></tr>
            )}
            {!loading && displayRows.map((r) => (
              <Fragment key={r.nonparish_cemetery_id}>
                <tr
                  onClick={() => toggleExpand(r)}
                  className={`border-b border-grid-border last:border-0 cursor-pointer hover:bg-cream-light ${
                    expandedId === r.nonparish_cemetery_id ? 'bg-cream-light' : ''
                  }`}
                >
                  <td className="px-4 py-2.5 text-navy font-bold">
                    {r.member_names || r.name}
                    {(r.member_count ?? 1) > 1 && (
                      <span className="ml-1.5 text-xs font-normal text-teal bg-teal/10 px-1.5 py-0.5 rounded">
                        {r.member_count} members
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-navy">{r.gender ?? '—'}</td>
                  <td className="px-4 py-2.5 text-navy">
                    {r.grave_number ?? '—'}
                    {(r.shared_grave_count ?? 0) > 0 && (
                      <span
                        className="ml-1.5 text-xs font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                        title="Another separate record uses this same grave code — likely should be merged as members of one grave."
                      >
                        shared
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-navy">{fmtDate(r.date_of_death)}</td>
                  <td className="px-4 py-2.5 text-navy">{fmtDate(r.date_of_burial)}</td>
                  <td className="px-4 py-2.5 text-navy">
                    {r.contact_person ?? '—'}{r.contact_phone ? ` · ${r.contact_phone}` : ''}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => openEdit(r, e)}
                        className="text-teal hover:underline text-xs mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => confirmDelete(r, e)}
                        className="btn-danger !px-2 !py-1 text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
                {expandedId === r.nonparish_cemetery_id && (
                  <tr className="border-b border-grid-border last:border-0 bg-cream-light/50">
                    <td colSpan={isAdmin ? 7 : 6} className="px-4 py-4">
                      {/* ── Members on this grave — a grave can hold more than
                          one person while still having exactly one subscription ── */}
                      <div className="bg-white border border-grid-border p-4 max-w-2xl mb-3">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs text-teal uppercase tracking-wide">
                            Members on grave {r.grave_number ?? '—'}
                          </p>
                          {isAdmin && !showAddMember && (
                            <button onClick={startAddMember} className="text-xs text-teal hover:text-navy underline">
                              + Add member
                            </button>
                          )}
                        </div>

                        {members.length === 0 && !showAddMember && (
                          <p className="text-sm text-teal">No members recorded yet.</p>
                        )}

                        {members.length > 0 && (
                          <table className="w-full text-xs font-georgia border border-grid-border mb-2">
                            <thead>
                              <tr className="bg-cream-light text-teal uppercase tracking-wide text-left">
                                <th className="px-2 py-1.5">Name</th>
                                <th className="px-2 py-1.5">Gender</th>
                                <th className="px-2 py-1.5">DOB</th>
                                <th className="px-2 py-1.5">Date of Death</th>
                                <th className="px-2 py-1.5">Date of Burial</th>
                                {isAdmin && <th className="px-2 py-1.5 text-right">Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {members.map((m) => (
                                <tr key={m.member_id} className="border-t border-grid-border">
                                  <td className="px-2 py-1.5 text-navy font-bold">{m.name}</td>
                                  <td className="px-2 py-1.5 text-navy">{m.gender ?? '—'}</td>
                                  <td className="px-2 py-1.5 text-navy">{fmtDate(m.dob)}</td>
                                  <td className="px-2 py-1.5 text-navy">{fmtDate(m.date_of_death)}</td>
                                  <td className="px-2 py-1.5 text-navy">{fmtDate(m.date_of_burial)}</td>
                                  {isAdmin && (
                                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                      <button onClick={() => startEditMember(m)} className="text-teal hover:underline mr-2">Edit</button>
                                      <button
                                        onClick={() => removeMember(r.nonparish_cemetery_id, m.member_id)}
                                        disabled={memberDeletingId === m.member_id}
                                        className="text-red-600 hover:underline disabled:opacity-50"
                                      >
                                        {memberDeletingId === m.member_id ? 'Removing…' : 'Remove'}
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {showAddMember && (
                          <div className="bg-cream-light/60 border border-grid-border rounded p-3 mt-2">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                              <div className="col-span-2 sm:col-span-1">
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Name *</label>
                                <input type="text" value={memberForm.name}
                                  onChange={(e) => updateMemberField('name', e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal" />
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Gender</label>
                                <select value={memberForm.gender}
                                  onChange={(e) => updateMemberField('gender', e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal">
                                  <option value="">—</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">DOB</label>
                                <input type="date" value={memberForm.dob}
                                  onChange={(e) => updateMemberField('dob', e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal" />
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Death</label>
                                <input type="date" value={memberForm.dateOfDeath}
                                  onChange={(e) => updateMemberField('dateOfDeath', e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal" />
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Burial</label>
                                <input type="date" value={memberForm.dateOfBurial}
                                  onChange={(e) => updateMemberField('dateOfBurial', e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal" />
                              </div>
                            </div>
                            {memberErr && <p className="text-xs text-red-600 mb-2">{memberErr}</p>}
                            <div className="flex items-center gap-2">
                              <button onClick={() => saveMember(r.nonparish_cemetery_id)} disabled={memberSaving}
                                className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                                {memberSaving ? 'Saving…' : editMemberId ? 'Update Member' : 'Add Member'}
                              </button>
                              <button onClick={cancelMemberForm} className="text-xs text-teal hover:text-navy underline">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="bg-white border border-grid-border p-4 max-w-2xl">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <p className="text-xs text-teal uppercase tracking-wide">Subscription</p>
                          <select
                            value={subYear}
                            onChange={(e) => changeSubYear(r.nonparish_cemetery_id, parseInt(e.target.value, 10))}
                            className="border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal"
                          >
                            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>

                        {subLoading ? (
                          <p className="text-sm text-teal">Loading…</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount</label>
                              <input type="number" step="0.01" value={subAmount} disabled={!isAdmin}
                                     onChange={(e) => setSubAmount(e.target.value)}
                                     className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light" />
                            </div>
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Paid Date</label>
                              <input type="date" value={subDate} disabled={!isAdmin}
                                     onChange={(e) => setSubDate(e.target.value)}
                                     className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light" />
                            </div>
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                              <select value={subStatus} disabled={!isAdmin}
                                      onChange={(e) => setSubStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                      className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light">
                                <option value="Pending">Pending</option>
                                <option value="Unpaid">Unpaid</option>
                                <option value="Paid">Paid</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {subErr && <p className="text-sm text-red-600 font-georgia mt-2">{subErr}</p>}

                        {isAdmin && (
                          <button onClick={() => saveSub(r.nonparish_cemetery_id)} disabled={subSaving || subLoading}
                                  className="btn-primary text-xs px-3 py-1.5 mt-3">
                            {subSaving ? 'Saving…' : 'Save Subscription'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile: compact cards */}
        <div className="sm:hidden divide-y divide-grid-border">
          {loading && <p className="px-4 py-6 text-center text-teal text-sm">Loading…</p>}
          {!loading && displayRows.length === 0 && (
            <p className="px-4 py-6 text-center text-teal text-sm">No records found.</p>
          )}
          {!loading && displayRows.map((r) => {
            const isExpanded = expandedId === r.nonparish_cemetery_id;
            return (
              <div key={r.nonparish_cemetery_id}>
                <div
                  onClick={() => toggleExpand(r)}
                  className={`p-3 cursor-pointer ${isExpanded ? 'bg-cream-light' : 'active:bg-cream-light'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-navy font-georgia truncate">{r.member_names || r.name}</p>
                      <p className="text-xs text-teal font-georgia">
                        Grave {r.grave_number ?? '—'}
                        {(r.shared_grave_count ?? 0) > 0 && (
                          <span className="ml-1.5 text-xs font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">shared</span>
                        )}
                      </p>
                    </div>
                    {(r.member_count ?? 1) > 1 && (
                      <span className="text-xs font-normal text-teal bg-teal/10 px-1.5 py-0.5 rounded flex-shrink-0">{r.member_count} members</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">{r.gender ?? '—'}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Died {fmtDate(r.date_of_death)}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Buried {fmtDate(r.date_of_burial)}</span>
                    {r.contact_person && (
                      <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">
                        {r.contact_person}{r.contact_phone ? ` · ${r.contact_phone}` : ''}
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={(e) => openEdit(r, e)} className="flex-1 btn-secondary !py-1.5 text-xs">Edit</button>
                      <button onClick={(e) => confirmDelete(r, e)} className="flex-1 btn-danger !py-1.5 text-xs">Delete</button>
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-grid-border bg-cream-light/50 p-3 space-y-3">
                    {/* Members on this grave */}
                    <div className="bg-white border border-grid-border p-3">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-teal uppercase tracking-wide">Members on grave {r.grave_number ?? '—'}</p>
                        {isAdmin && !showAddMember && (
                          <button onClick={startAddMember} className="text-xs text-teal hover:text-navy underline">+ Add</button>
                        )}
                      </div>
                      {members.length === 0 && !showAddMember && (
                        <p className="text-sm text-teal">No members recorded yet.</p>
                      )}
                      {members.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {members.map((m) => (
                            <div key={m.member_id} className="border border-grid-border rounded p-2">
                              <p className="font-bold text-navy text-xs font-georgia">{m.name}</p>
                              <p className="text-xs text-gray-500 font-georgia mt-0.5">
                                {m.gender ?? '—'} · DOB {fmtDate(m.dob)} · Died {fmtDate(m.date_of_death)} · Buried {fmtDate(m.date_of_burial)}
                              </p>
                              {isAdmin && (
                                <div className="flex gap-3 mt-1.5">
                                  <button onClick={() => startEditMember(m)} className="text-teal hover:underline text-xs">Edit</button>
                                  <button
                                    onClick={() => removeMember(r.nonparish_cemetery_id, m.member_id)}
                                    disabled={memberDeletingId === m.member_id}
                                    className="text-red-600 hover:underline text-xs disabled:opacity-50"
                                  >
                                    {memberDeletingId === m.member_id ? 'Removing…' : 'Remove'}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {showAddMember && (
                        <div className="bg-cream-light/60 border border-grid-border rounded p-3 mt-2 space-y-2">
                          <div>
                            <label className="block text-xs text-teal uppercase tracking-wide mb-1">Name *</label>
                            <input type="text" value={memberForm.name}
                              onChange={(e) => updateMemberField('name', e.target.value)}
                              className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Gender</label>
                              <select value={memberForm.gender}
                                onChange={(e) => updateMemberField('gender', e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal">
                                <option value="">—</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">DOB</label>
                              <input type="date" value={memberForm.dob}
                                onChange={(e) => updateMemberField('dob', e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                            </div>
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Death</label>
                              <input type="date" value={memberForm.dateOfDeath}
                                onChange={(e) => updateMemberField('dateOfDeath', e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                            </div>
                            <div>
                              <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Burial</label>
                              <input type="date" value={memberForm.dateOfBurial}
                                onChange={(e) => updateMemberField('dateOfBurial', e.target.value)}
                                className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal" />
                            </div>
                          </div>
                          {memberErr && <p className="text-xs text-red-600">{memberErr}</p>}
                          <div className="flex gap-2">
                            <button onClick={() => saveMember(r.nonparish_cemetery_id)} disabled={memberSaving}
                              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50 flex-1">
                              {memberSaving ? 'Saving…' : editMemberId ? 'Update Member' : 'Add Member'}
                            </button>
                            <button onClick={cancelMemberForm} className="text-xs text-teal hover:text-navy underline">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Subscription */}
                    <div className="bg-white border border-grid-border p-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <p className="text-xs text-teal uppercase tracking-wide">Subscription</p>
                        <select
                          value={subYear}
                          onChange={(e) => changeSubYear(r.nonparish_cemetery_id, parseInt(e.target.value, 10))}
                          className="border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal"
                        >
                          {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      {subLoading ? (
                        <p className="text-sm text-teal">Loading…</p>
                      ) : (
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount</label>
                            <input type="number" step="0.01" value={subAmount} disabled={!isAdmin}
                                   onChange={(e) => setSubAmount(e.target.value)}
                                   className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light" />
                          </div>
                          <div>
                            <label className="block text-xs text-teal uppercase tracking-wide mb-1">Paid Date</label>
                            <input type="date" value={subDate} disabled={!isAdmin}
                                   onChange={(e) => setSubDate(e.target.value)}
                                   className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light" />
                          </div>
                          <div>
                            <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                            <select value={subStatus} disabled={!isAdmin}
                                    onChange={(e) => setSubStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                    className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light">
                              <option value="Pending">Pending</option>
                              <option value="Unpaid">Unpaid</option>
                              <option value="Paid">Paid</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {subErr && <p className="text-sm text-red-600 font-georgia mt-2">{subErr}</p>}
                      {isAdmin && (
                        <button onClick={() => saveSub(r.nonparish_cemetery_id)} disabled={subSaving || subLoading}
                                className="btn-primary text-xs px-3 py-1.5 mt-3 w-full">
                          {subSaving ? 'Saving…' : 'Save Subscription'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <PaginationBar page={page} pageSize={pageSize} total={total} totalPages={totalPages}
                     onPage={setPage} onPageSize={handlePageSize} />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">Delete Record</h2>
            <p className="text-sm text-gray-600 mb-4">
              Delete record for <span className="font-semibold">{deleteTarget.name}</span>? This cannot be undone.
            </p>
            {deleteErr && <p className="text-red-600 text-sm mb-3">{deleteErr}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={cancelDelete} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
